import { CheckIcon, CopyIcon, RotateCcwIcon } from 'components/icons';
import { Bubble, BubbleContent } from 'components/ui/bubble';
import { Button } from 'components/ui/button';
import { Message, MessageContent } from 'components/ui/message';
import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { PortalChatMessage } from 'types/aiChat';
import { CopiedStatus } from 'components/util/CopiedStatus';
import { cn } from 'utils/cn';
import { useAiChatOptionPick } from '../aiChatOptionPickContext';
import { useCopied } from '../hooks/useCopied';
import { AiChatTableDownloadProvider } from '../aiChatTableDownloadContext';
import { buildMessageBlocks } from '../utils/messageBlocks';
import { buildMessageClipboardData, copiedText, writeClipboardData } from '../utils/messageClipboard';
import { extractToolCallHistory } from '../utils/toolCallParts';
import AiChatFeedback, { AnswerAction } from './AiChatFeedback';
import MeetingPrepActions from './MeetingPrepActions';
import { ChartFigure } from './message/figure/ChartFigure';
import { figureTile } from './message/figure/figureMeta';
import FigurePart from './message/figure/FigurePart';
import { FileCardSlot } from './message/figure/RowsFigure';
import MessageTextPart from './message/MessageTextPart';
import { OptionsToolPart } from './message/OptionsToolPart';
import AiChatProgress from './progress/AiChatProgress';
import { Spinner } from './progress/Spinner';

interface Props {
    message: PortalChatMessage;
    isStreaming?: boolean;
    isLastMessage?: boolean;
    /**
     * The title of the message's thread, empty while it has none. It names the file every table of a finished answer
     * downloads as; without it an older answer's markdown table has no Download, and a Figure names its file after the
     * assistant.
     */
    threadTitle?: string;
    /** The user stopped this answer: it says so, with Retry, in place of its actions. */
    stopped?: boolean;
    /** A question's message that is one of the options the answer before it offered: it leads with a check. */
    picked?: boolean;
}

// The searches whose rows a record choice offers; their tile is the choice's.
const RECORD_SEARCH = /^(search|semanticSearch)[A-Z]/;

/** Retry on the last answer, when the surface offers one. */
function RetryAction() {
    const intl = useIntl();
    const { retry, announce } = useAiChatOptionPick();
    if (!retry) {
        return null;
    }
    return (
        <AnswerAction
            label={intl.formatMessage({ id: 'ai-chat.retry' })}
            onClick={() => {
                announce(intl.formatMessage({ id: 'ai-chat.error.retrying' }));
                retry();
            }}
        >
            <RotateCcwIcon className="size-4" />
        </AnswerAction>
    );
}

/** "You stopped this answer", and Retry, which stays in place, busy, until the answer is asked again. */
function StoppedLine() {
    const intl = useIntl();
    const { retry, announce } = useAiChatOptionPick();
    const [retrying, setRetrying] = useState(false);
    const retryingLabel = intl.formatMessage({ id: 'ai-chat.error.retrying' });

    return (
        <div className="flex flex-wrap items-center gap-2">
            <span className="bg-neutral-tint text-caption text-foreground-secondary inline-flex items-center gap-1.5 rounded-lg px-1.5 font-medium">
                <span aria-hidden="true" className="size-2 rounded-[2px] bg-current" />
                {intl.formatMessage({ id: 'ai-chat.stopped' })}
            </span>
            {retry ? (
                <Button
                    variant="ghost"
                    aria-disabled={retrying || undefined}
                    onClick={() => {
                        if (retrying) return;
                        setRetrying(true);
                        announce(retryingLabel);
                        retry();
                    }}
                    className="text-meta text-muted-foreground h-7 gap-1.5 rounded-md ps-1.5 pe-2 font-normal aria-disabled:cursor-default"
                >
                    {retrying ? (
                        <span className="grid size-3.5 place-items-center" aria-hidden>
                            <Spinner className="size-3" />
                        </span>
                    ) : (
                        <RotateCcwIcon className="size-3.5" />
                    )}
                    {retrying ? retryingLabel : intl.formatMessage({ id: 'ai-chat.retry' })}
                </Button>
            ) : null}
        </div>
    );
}

const AiChatMessage: React.FC<Props> = ({ message, isStreaming, isLastMessage, threadTitle, stopped, picked }) => {
    const intl = useIntl();
    const [isCopied, markCopied] = useCopied();
    const [fileCardSlot, setFileCardSlot] = useState<HTMLDivElement | null>(null);

    const isUser = message.role === 'user';

    const { blocks, toolCalls, hasText, copyable, onlyAsks, hasFileCard, choiceTileClassName } = useMemo(() => {
        const text = message.parts.flatMap((part) => (part.type === 'text' && part.text ? [part.text] : [])).join('\n');
        const toolCalls = extractToolCallHistory(message.parts, message.metadata?.toolSummaries);
        // A record choice offers the candidates the turn's last search found, so an untyped option takes that
        // search's tile.
        const search = toolCalls.findLast((toolCall) => RECORD_SEARCH.test(toolCall.name));
        const blocks = buildMessageBlocks({ text, toolCalls, isStreaming });

        return {
            blocks,
            toolCalls,
            hasText: blocks.some((block) => block.kind === 'text'),
            // Copy shows when it has something to copy: the text, or a draft's text.
            copyable: blocks.some((block) => copiedText(block).length > 0),
            // An answer that only asks: its choice, maybe a line of prose, and no Figure answering anything yet.
            onlyAsks:
                blocks.some((block) => block.kind === 'options') &&
                !blocks.some((block) => block.kind === 'figure' || block.kind === 'chart'),
            hasFileCard: blocks.some((block) => block.kind === 'figure' && block.fileFormat != null),
            choiceTileClassName: figureTile({ kind: 'list', source: search && { tool: search.name } }).className,
        };
    }, [message, isStreaming]);

    const aiChatMessageId = message.metadata?.aiChatMessageId;
    const roleLabel = intl.formatMessage({ id: isUser ? 'ai-chat.role.user' : 'ai-chat.role.assistant' });
    const copyLabel = intl.formatMessage({ id: 'ai-chat.copy' });
    // Only the text still arriving animates its words and carries the caret.
    const lastTextIndex = !isUser && isStreaming ? blocks.findLastIndex((block) => block.kind === 'text') : -1;

    const handleCopy = async () => {
        try {
            await writeClipboardData(buildMessageClipboardData(blocks));
            markCopied();
        } catch (error) {
            console.error('Failed to copy message:', error);
        }
    };

    const content = (
        <AiChatTableDownloadProvider value={!isUser && !isStreaming ? (threadTitle ?? null) : null}>
            {blocks.map((block, index) => {
                if (block.kind === 'text') {
                    const previous = blocks[index - 1];
                    return (
                        <MessageTextPart
                            key={block.key}
                            text={block.text}
                            fileFormat={previous?.kind === 'file' ? previous.format : undefined}
                            streaming={index === lastTextIndex}
                        />
                    );
                }
                // Its card is drawn by the table it names, in the text block that follows.
                if (block.kind === 'file') {
                    return null;
                }
                if (block.kind === 'figure') {
                    return (
                        <FigurePart
                            key={block.key}
                            definition={block.definition}
                            fileFormat={block.fileFormat}
                            message={message}
                        />
                    );
                }
                if (block.kind === 'chart') {
                    return <ChartFigure key={block.key} spec={block.spec} />;
                }
                if (block.kind === 'options') {
                    return (
                        <OptionsToolPart
                            key={block.key}
                            spec={block.spec}
                            isLatest={isLastMessage === true}
                            recordTileClassName={choiceTileClassName}
                        />
                    );
                }
                return (
                    <p key={block.key} className="text-body2 text-error">
                        {intl.formatMessage({
                            id:
                                block.kind === 'options-error'
                                    ? 'ai-chat.error.options-render-failed'
                                    : 'ai-chat.error.chart-render-failed',
                        })}
                    </p>
                );
            })}
        </AiChatTableDownloadProvider>
    );

    if (isUser) {
        return (
            <Message align="end" role="article" aria-label={roleLabel}>
                <Bubble align="end" variant="muted" className="max-w-[85%]">
                    <BubbleContent
                        className={cn(
                            'text-body1 text-foreground rounded-2xl rounded-ee-md px-3.5 py-2 text-pretty',
                            picked && 'flex items-center gap-1.5'
                        )}
                    >
                        {picked ? <CheckIcon className="text-primary size-4 shrink-0" /> : null}
                        {content}
                    </BubbleContent>
                </Bubble>
            </Message>
        );
    }

    return (
        <Message role="article" aria-label={roleLabel}>
            <MessageContent className="text-body1 text-foreground gap-3">
                <AiChatProgress
                    message={message}
                    toolCalls={toolCalls}
                    isStreaming={isStreaming === true}
                    hasText={hasText}
                />

                {/* A Figure's file card leads the answer, before its opening text: the Figure draws it here. */}
                {hasFileCard ? <div ref={setFileCardSlot} className="flex flex-col gap-3 empty:hidden" /> : null}
                <FileCardSlot.Provider value={fileCardSlot}>{content}</FileCardSlot.Provider>

                <MeetingPrepActions message={message} isStreaming={isStreaming} />

                {/* An answer that only asks ends at its choice: the reply is the pick, so it takes no actions. */}
                {stopped ? (
                    <StoppedLine />
                ) : onlyAsks ? null : isStreaming ? (
                    // The row holds the height of the actions that land in it, so the answer does not grow under the
                    // reader when it finishes.
                    <div className="min-h-7" />
                ) : (
                    <AiChatFeedback aiChatMessageId={typeof aiChatMessageId === 'number' ? aiChatMessageId : undefined}>
                        {copyable ? (
                            <>
                                <AnswerAction
                                    label={copyLabel}
                                    closeOnClick={false}
                                    onClick={() => {
                                        void handleCopy();
                                    }}
                                >
                                    <span className="t-icon-swap" data-state={isCopied ? 'b' : 'a'} aria-hidden="true">
                                        <span className="t-icon" data-icon="a">
                                            <CopyIcon className="size-4" />
                                        </span>
                                        <span className="t-icon text-success-ink" data-icon="b">
                                            <CheckIcon className="size-4 stroke-2!" />
                                        </span>
                                    </span>
                                </AnswerAction>
                                <CopiedStatus copied={isCopied} />
                            </>
                        ) : null}
                        {isLastMessage ? <RetryAction /> : null}
                    </AiChatFeedback>
                )}
            </MessageContent>
        </Message>
    );
};

export default React.memo(AiChatMessage, (prev, next) => {
    return (
        prev.message === next.message &&
        prev.isStreaming === next.isStreaming &&
        prev.isLastMessage === next.isLastMessage &&
        prev.threadTitle === next.threadTitle &&
        prev.stopped === next.stopped &&
        prev.picked === next.picked
    );
});
